import React from "react";
import styled from '@emotion/styled';
import { Sizes, themeColor } from '../../../components/Theme';
import { UncoverQuestionDescription } from './UncoverQuestionDescription';
import { media } from '../../media';
import { DownloadFile } from '../../../common/DownloadFile';
import { FileQuestion, QuestionCaseInfo } from '../../../api/types/questions';
import { FormattedMessage } from 'react-intl';
import { MarkdownDisplay } from '../../../toolympus/components/schemed/Markdown';
import { AttributeLine, AttributesWrapper, QuestionCaseVoteProps, QuestionVoteDates } from "./QuestionVoteDates";
import { EntityText } from "../Questions.style";
import { TableDisplay } from "../../../toolympus/components/schemed/TableDisplay";
import { FieldType } from "../../../toolympus/hooks/useSchema";
import { useFormats } from "../../../toolympus/components/schemed";
import { dict } from "../../../constants/system_reference";

const BREAK_POINT_FOR_DESCRIPTION = 245;

interface QuestionCaseDescriptionProps extends QuestionCaseVoteProps {
    comments: string,
    documents: FileQuestion[]
    questionId: string,
    isConflict: boolean
    caseInfo?: QuestionCaseInfo | null;
    isInternational?: boolean;
}


export const QuestionCaseDescription = (props: QuestionCaseDescriptionProps) => {
    const { comments, documents, questionId, caseInfo } = props;
    const { formatDecimal } = useFormats();
    return (
      <Wrapper>
        
        <Content>

          {!!comments && <>
            <Title><FormattedMessage id="service.description" /></Title>
            <DescriptionFull>
                <MarkdownDisplay text={comments} />
            </DescriptionFull>

            <Description>
                {(comments?.length || 0) - 1 <= BREAK_POINT_FOR_DESCRIPTION 
                    ? <MarkdownDisplay text={comments} /> 
                    : <UncoverQuestionDescription comments={comments} breakPoint={BREAK_POINT_FOR_DESCRIPTION} /> 
                }
            </Description>
          </>}

          {(!!caseInfo?.case_price_amount || !!caseInfo?.arbitration_procedure) &&
            <AttributesWrapper>

              {!!props.isInternational && !!caseInfo?.applicable_law &&
                <AttributeLine>
                  <EntityText>
                    <FormattedMessage id="question.case_info.applicable_law" />
                  </EntityText>
                  <EntityText>
                    <TableDisplay
                      field="applicable_law"
                      row={caseInfo}
                      schema={{ type: FieldType.text }}
                      />
                  </EntityText>
                </AttributeLine>}

              {!!props.isInternational && !!caseInfo?.place_of_arbitration_country &&
                <AttributeLine>
                  <EntityText>
                    <FormattedMessage id="question.case_info.place_of_arbitration_country" />
                  </EntityText>
                  <EntityText>
                    <TableDisplay
                      field="place_of_arbitration_country"
                      row={caseInfo}
                      schema={{ type: FieldType.dictionarySelect, dictionary: "Country" }}
                      />
                  </EntityText>
                </AttributeLine>}


                
              {!!caseInfo?.case_price_amount &&
                <AttributeLine>
                  <EntityText>
                    <FormattedMessage id="question.case_info.case_price_amount" />
                  </EntityText>
                  <EntityText>
                    {caseInfo?.case_price_currency || ""}
                    {" "}
                    {formatDecimal(caseInfo?.case_price_amount || "")}
                  </EntityText>
                </AttributeLine>}

              {!!caseInfo?.reward_fee_amount &&
                <AttributeLine>
                  <EntityText>
                    <FormattedMessage id="question.case_info.reward_fee_amount" />
                  </EntityText>
                  <EntityText>
                    {caseInfo?.arbitration_fee_currency || ""}
                    {" "}
                    {formatDecimal(caseInfo?.reward_fee_amount || "")}
                  </EntityText>
                </AttributeLine>}


              {!!caseInfo?.arbitration_procedure &&
                <AttributeLine>
                  <EntityText>
                    <FormattedMessage id="question.case_info.arbitration_procedure" />
                  </EntityText>
                  <EntityText>
                    <TableDisplay
                      field="arbitration_procedure"
                      row={caseInfo}
                      schema={{ type: FieldType.dictionarySelect, dictionary: "ArbitrationProcedure" }}
                      />
                  </EntityText>
                </AttributeLine>}


              {!!caseInfo?.arbitrators_count &&
                <AttributeLine>
                  <EntityText>
                    <FormattedMessage id="question.case_info.arbitrators_count" />
                  </EntityText>
                  <EntityText>
                    <TableDisplay
                      field="arbitrators_count"
                      row={caseInfo}
                      schema={{ type: FieldType.number }}
                      />
                  </EntityText>
                </AttributeLine>}

              
            </AttributesWrapper>}

          <QuestionVoteDates
              question_type={props.question_type}
              start_date={props.start_date}
              end_date={props.end_date}
              />
        </Content>

        <Files>
            {documents.map(d => <DownloadFile file={d} questionId={questionId} key={d._id} />)}
        </Files>

      </Wrapper>)
}

const Wrapper = styled.div`
    width: 60%;
    @media ${media.l} {
        width: 100%;
    }
`;

const Title = styled.p`
    margin: 0;
    font: 300 1rem "Ubuntu";
    color: ${themeColor(c => c.textGreyBasic)};

    @media ${media.m} {
        text-align: center;
    }
`;

const Description = styled.p`
    display: none;
    font: 300 1rem "Ubuntu";
    color: ${themeColor(c => c.blackDefault)};

    @media ${media.m} {
        display: block;
        font-size: 14px;
    }
`;

const Files = styled.div`
    margin: 0;
    display: grid;
    width: 100%;
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;

    @media ${media.l} {
        gap: 20px;
    }

    @media ${media.m} {
        padding: 5%;
        width: auto;
        gap: 5px 10px;
        background-color: ${themeColor(c => c.question_case.descriptionBackground)};
    }

    @media ${media.s} {
        grid-template-columns: repeat(2, 1fr);
    }

    @media ${media.xs} {
        grid-template-columns: 1fr;
    }
`;

const Content = styled.div`
    width: auto;
    margin: 0 0 20px 0;
    padding: 5%;
    background-color: ${themeColor(c => c.question_case.descriptionBackground)};
    border-radius: ${Sizes.geometry.borderRadius.pannel}px;
    word-break: break-word;
    box-shadow: ${dict.spl.boxShadow.average} inset ${themeColor(c => c.blackShadow)};

    @media ${media.m} {
        margin: 2rem 0 0 0;
    }
`;

const DescriptionFull = styled.p`
    font: 300 1rem "Ubuntu";
    color: ${themeColor(c => c.blackDefault)};

    @media ${media.m} {
        display: none;
    }
`;